import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Phone, UserX, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { toast } from "sonner";

interface AbsentStudent {
  attendance_id: string;
  student_id: string;
  student_name: string;
  class_name: string;
  subject_class_id: string;
  class_date: string;
  has_note: boolean;
  recorded_by?: string;
}

export const AbsenceFollowUpWidget = () => {
  const queryClient = useQueryClient();
  const [selectedAbsence, setSelectedAbsence] = useState<AbsentStudent | null>(null);
  const [noteForm, setNoteForm] = useState({
    reason: "",
    contacted_person: "",
    notes: "",
  });

  // Fetch today's absences that don't have notes
  const { data: absences, isLoading } = useQuery({
    queryKey: ["today-absences"],
    queryFn: async () => {
      const today = format(new Date(), "yyyy-MM-dd");
      
      // Get today's absences with class details
      const { data: attendanceData, error: attendanceError } = await supabase
        .from("subject_class_attendance")
        .select(`
          id,
          student_id,
          subject_class_id,
          class_date,
          subject_classes(id, class_name)
        `)
        .eq("status", "Absent")
        .eq("class_date", today);

      if (attendanceError) throw attendanceError;

      if (!attendanceData || attendanceData.length === 0) return [];

      // Get student names
      const studentIds = [...new Set(attendanceData.map(a => a.student_id))];
      const { data: students } = await supabase
        .from("students")
        .select("id, first_name, last_name")
        .in("id", studentIds);

      const studentMap = new Map(students?.map(s => [s.id, `${s.first_name} ${s.last_name || ""}`.trim()]));

      // Check which absences already have notes, including who recorded them
      const { data: existingNotes } = await supabase
        .from("student_absence_notes")
        .select("student_id, absence_date, subject_class_id, contacted_by")
        .eq("absence_date", today);

      // Get profile names for who recorded
      const contactedByIds = [...new Set(existingNotes?.map(n => n.contacted_by).filter(Boolean))];
      let profileMap = new Map();
      if (contactedByIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", contactedByIds);
        profileMap = new Map(profiles?.map(p => [p.id, p.full_name]));
      }

      const noteMap = new Map(
        existingNotes?.map(n => [
          `${n.student_id}-${n.absence_date}-${n.subject_class_id}`,
          { has_note: true, recorded_by: profileMap.get(n.contacted_by) || "Unknown" }
        ])
      );

      return attendanceData.map(a => {
        const classInfo = a.subject_classes as { id: string; class_name: string } | null;
        const noteInfo = noteMap.get(`${a.student_id}-${a.class_date}-${a.subject_class_id}`);
        return {
          attendance_id: a.id,
          student_id: a.student_id,
          student_name: studentMap.get(a.student_id) || "Unknown",
          class_name: classInfo?.class_name || "Unknown Class",
          subject_class_id: a.subject_class_id,
          class_date: a.class_date,
          has_note: !!noteInfo,
          recorded_by: noteInfo?.recorded_by,
        };
      }) as AbsentStudent[];
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const addNoteMutation = useMutation({
    mutationFn: async (data: {
      student_id: string;
      absence_date: string;
      subject_class_id: string;
      reason: string;
      contacted_person: string;
      notes: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase.from("student_absence_notes").insert({
        student_id: data.student_id,
        absence_date: data.absence_date,
        subject_class_id: data.subject_class_id,
        reason: data.reason,
        contacted_person: data.contacted_person,
        contacted_by: user?.id,
        notes: data.notes,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["today-absences"] });
      toast.success("Absence note recorded");
      setSelectedAbsence(null);
      setNoteForm({ reason: "", contacted_person: "", notes: "" });
    },
    onError: () => {
      toast.error("Failed to record note");
    },
  });

  const handleSaveNote = () => {
    if (!selectedAbsence || !noteForm.reason || !noteForm.contacted_person) {
      toast.error("Please fill in required fields");
      return;
    }

    addNoteMutation.mutate({
      student_id: selectedAbsence.student_id,
      absence_date: selectedAbsence.class_date,
      subject_class_id: selectedAbsence.subject_class_id,
      reason: noteForm.reason,
      contacted_person: noteForm.contacted_person,
      notes: noteForm.notes,
    });
  };

  const pendingFollowUps = absences?.filter(a => !a.has_note) || [];
  const completedFollowUps = absences?.filter(a => a.has_note) || [];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!absences || absences.length === 0) {
    return null; // Don't show widget if no absences today
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" />
            Absence Follow-Up
            {pendingFollowUps.length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {pendingFollowUps.length} pending
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingFollowUps.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <p className="text-sm">All absences followed up!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pendingFollowUps.map(absence => (
                <div
                  key={absence.attendance_id}
                  className="flex items-center justify-between p-3 rounded-lg border border-red-500/20 bg-red-500/5"
                >
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <UserX className="h-4 w-4 text-red-500" />
                      {absence.student_name}
                    </p>
                    <p className="text-sm text-muted-foreground">{absence.class_name}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedAbsence(absence)}
                  >
                    <Phone className="h-4 w-4 mr-1" />
                    Record
                  </Button>
                </div>
              ))}
            </div>
          )}

          {completedFollowUps.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-2">
                Completed: {completedFollowUps.length}
              </p>
              <div className="space-y-1">
                {completedFollowUps.slice(0, 3).map(absence => (
                  <div
                    key={absence.attendance_id}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    <span>{absence.student_name} - {absence.class_name}</span>
                    {absence.recorded_by && (
                      <span className="text-xs text-primary/70">(by {absence.recorded_by})</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedAbsence} onOpenChange={(open) => !open && setSelectedAbsence(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Absence Reason</DialogTitle>
          </DialogHeader>
          
          {selectedAbsence && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="font-medium">{selectedAbsence.student_name}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedAbsence.class_name} • {format(new Date(selectedAbsence.class_date), "MMM d, yyyy")}
                </p>
              </div>

              <div>
                <Label>Who was contacted? *</Label>
                <Select
                  value={noteForm.contacted_person}
                  onValueChange={(value) => setNoteForm({ ...noteForm, contacted_person: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Student">Student</SelectItem>
                    <SelectItem value="Parent">Parent</SelectItem>
                    <SelectItem value="Guardian">Guardian</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Reason for Absence *</Label>
                <Input
                  value={noteForm.reason}
                  onChange={(e) => setNoteForm({ ...noteForm, reason: e.target.value })}
                  placeholder="e.g., Medical appointment, Family emergency"
                />
              </div>

              <div>
                <Label>Additional Notes</Label>
                <Textarea
                  value={noteForm.notes}
                  onChange={(e) => setNoteForm({ ...noteForm, notes: e.target.value })}
                  placeholder="Any additional details..."
                  rows={3}
                />
              </div>

              <Button
                onClick={handleSaveNote}
                className="w-full"
                disabled={addNoteMutation.isPending}
              >
                {addNoteMutation.isPending ? "Saving..." : "Save Note"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
